import { useState } from 'react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { StickyNote, X, Check } from 'lucide-react';
import { toast } from 'sonner';
import { STATES, LOBS } from './UploadModal';

const API = process.env.REACT_APP_BACKEND_URL;

export const NoteModal = ({ headers, carriers, onClose, onSubmitted }) => {
  const [title, setTitle] = useState('');
  const [selected, setSelected] = useState([]);
  const [state, setState] = useState('');
  const [lob, setLob] = useState('');
  const [content, setContent] = useState('');
  const [sourceNote, setSourceNote] = useState('');
  const [effectiveDate, setEffectiveDate] = useState('');
  const [saving, setSaving] = useState(false);

  const toggleCarrier = (c) =>
    setSelected(prev => (prev.includes(c) ? prev.filter(x => x !== c) : [...prev, c]));

  const valid = title.trim().length >= 3 && selected.length > 0 && content.trim().length >= 10;

  const submit = async (e) => {
    e.preventDefault();
    if (!valid) return;
    setSaving(true);
    try {
      const res = await fetch(`${API}/api/notes`, {
        method: 'POST', headers: headers(),
        body: JSON.stringify({ title, carriers: selected, state, lob, content, source_note: sourceNote, effective_date: effectiveDate }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) { toast.error(data.detail || 'Could not submit note'); return; }
      toast.success('Guideline note submitted — the approver has been notified');
      onSubmitted();
    } catch { toast.error('Network error — please try again'); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-start justify-center pt-8 px-4 overflow-y-auto" onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <form onSubmit={submit} className="bg-white rounded-xl shadow-2xl w-full max-w-lg mb-8" data-testid="note-modal">
        <div className="px-5 py-3 bg-[#101C47] rounded-t-xl flex items-center justify-between">
          <span className="font-bold text-sm text-white flex items-center gap-2"><StickyNote size={14} /> Add Guideline Note</span>
          <button type="button" onClick={onClose} className="text-white/60 hover:text-white"><X size={16} /></button>
        </div>
        <div className="p-5 space-y-3 max-h-[70vh] overflow-y-auto">
          <p className="text-[11px] text-gray-400 -mt-1">Share a one-off underwriting rule you learned (e.g., from an underwriter email or call). It goes to the approver before becoming searchable in AI Chat.</p>

          <div>
            <label className="text-[10px] font-semibold text-gray-500 block mb-1">Title * <span className="text-gray-300 font-normal">(short summary, 3-150 chars)</span></label>
            <Input value={title} onChange={e => setTitle(e.target.value)} maxLength={150} placeholder="e.g., Frontline now declines homes with polybutylene plumbing" className="h-8 text-xs" data-testid="note-title" />
          </div>

          <div>
            <label className="text-[10px] font-semibold text-gray-500 block mb-1">Applies to Carrier(s) * <span className="text-gray-300 font-normal">(select one or more)</span></label>
            <div className="border rounded-lg p-2 max-h-32 overflow-y-auto grid grid-cols-2 gap-1" data-testid="note-carriers">
              {carriers.map(c => (
                <button type="button" key={c} onClick={() => toggleCarrier(c)}
                  className={`flex items-center gap-1.5 text-[11px] px-2 py-1 rounded text-left transition-colors ${selected.includes(c) ? 'bg-[#101C47] text-white' : 'text-gray-600 hover:bg-gray-100'}`}
                  data-testid={`note-carrier-${c.replace(/\s+/g, '-')}`}>
                  <span className={`w-3 h-3 rounded-sm border flex items-center justify-center flex-shrink-0 ${selected.includes(c) ? 'border-white/50' : 'border-gray-300'}`}>
                    {selected.includes(c) && <Check size={9} />}
                  </span>
                  <span className="truncate">{c}</span>
                </button>
              ))}
            </div>
            {selected.length > 0 && <p className="text-[10px] text-gray-400 mt-1">{selected.length} selected: {selected.join(', ')}</p>}
          </div>

          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className="text-[10px] font-semibold text-gray-500 block mb-1">State</label>
              <select value={state} onChange={e => setState(e.target.value)} className="w-full h-8 text-xs border rounded-md px-2 bg-white" data-testid="note-state">
                <option value="">All States</option>
                {STATES.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
            <div>
              <label className="text-[10px] font-semibold text-gray-500 block mb-1">Line of Business</label>
              <select value={lob} onChange={e => setLob(e.target.value)} className="w-full h-8 text-xs border rounded-md px-2 bg-white" data-testid="note-lob">
                <option value="">Any LOB</option>
                {LOBS.map(l => <option key={l} value={l}>{l}</option>)}
              </select>
            </div>
            <div>
              <label className="text-[10px] font-semibold text-gray-500 block mb-1">Effective Date</label>
              <Input type="date" value={effectiveDate} onChange={e => setEffectiveDate(e.target.value)} className="h-8 text-xs" data-testid="note-effective-date" />
            </div>
          </div>

          <div>
            <label className="text-[10px] font-semibold text-gray-500 block mb-1">Guideline Details * <span className="text-gray-300 font-normal">(10-5000 chars — what's the rule?)</span></label>
            <textarea value={content} onChange={e => setContent(e.target.value)} maxLength={5000}
              placeholder="e.g., Per underwriter Jane Smith (email 6/12), Frontline will no longer accept homes with polybutylene plumbing in coastal SC counties, effective immediately. Existing policies are grandfathered until renewal."
              className="w-full text-xs p-2 border rounded-lg h-24 resize-none" data-testid="note-content" />
            <p className="text-[9px] text-gray-300 text-right">{content.length}/5000</p>
          </div>

          <div>
            <label className="text-[10px] font-semibold text-gray-500 block mb-1">Source <span className="text-gray-300 font-normal">(optional — where did you learn this?)</span></label>
            <Input value={sourceNote} onChange={e => setSourceNote(e.target.value)} maxLength={300} placeholder="e.g., Email from Frontline underwriter, 6/12/2026" className="h-8 text-xs" data-testid="note-source" />
          </div>

          <div className="flex gap-2 pt-2">
            <Button type="submit" disabled={saving || !valid} className="flex-1 h-9 text-xs bg-[#101C47]" data-testid="note-submit">
              {saving ? 'Submitting…' : 'Submit for Approval'}
            </Button>
            <Button type="button" variant="outline" onClick={onClose} className="h-9 text-xs">Cancel</Button>
          </div>
        </div>
      </form>
    </div>
  );
};
